import { useState } from 'react';
import { uploadPhoto } from '@/lib/api';
import { getDeviceId } from '@/lib/device';

export interface UploadResult {
  ok: boolean;
  error?: string;
}

export interface BatchUploadResult {
  total: number;
  successful: number;
  failed: number;
  errors: string[];
}

export function useUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const upload = async (opts: {
    uploadedBy: string;
    caption?: string;
    file: Blob;
  }): Promise<UploadResult> => {
    setIsUploading(true);
    setError(null);
    setProgress(10);
    try {
      const visitorId = getDeviceId();
      setProgress(30);
      const r = await uploadPhoto({ ...opts, visitorId });
      setProgress(100);
      return { ok: true, ...r };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setIsUploading(false);
    }
  };

  const uploadBatch = async (
    files: Blob[],
    uploadedBy: string,
    onStep?: (completed: number, total: number) => void
  ): Promise<BatchUploadResult> => {
    setIsUploading(true);
    setError(null);
    const visitorId = getDeviceId();
    let successful = 0;
    let failed = 0;
    const errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      try {
        await uploadPhoto({
          uploadedBy,
          visitorId,
          file: files[i],
        });
        successful++;
      } catch (e) {
        failed++;
        const msg =
          e instanceof Error
            ? e.message
            : typeof e === 'string'
            ? e
            : (e as any)?.message || JSON.stringify(e);
        errors.push(msg);
      }
      const percent = Math.round(((i + 1) / files.length) * 100);
      setProgress(percent);
      onStep?.(i + 1, files.length);
    }

    setIsUploading(false);
    return {
      total: files.length,
      successful,
      failed,
      errors,
    };
  };

  const reset = () => {
    setError(null);
    setProgress(0);
  };

  return { upload, uploadBatch, isUploading, progress, error, reset };
}
